import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";

interface AssignSubcontractorSheetProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: Id<"serviceLocations">;
  onSubcontractorAssigned?: () => void;
}

export function AssignSubcontractorSheet({
  isOpen,
  onClose,
  locationId,
  onSubcontractorAssigned,
}: AssignSubcontractorSheetProps) {
  const [selectedSubcontractorId, setSelectedSubcontractorId] = useState<string>("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [status, setStatus] = useState<string>("active");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch active subcontractors
  const subcontractors = useQuery(api.subcontractors.getActiveSubcontractors) ?? [];

  // Assign subcontractor mutation
  const assignSubcontractor = useMutation(api.serviceLocations.assignSubcontractor);

  // Get available services from selected subcontractor
  const selectedSubcontractor = subcontractors.find(
    (sub: any) => sub._id === selectedSubcontractorId
  );
  const availableServices = selectedSubcontractor?.services ?? [];

  // Handle service selection
  const handleServiceToggle = (service: string) => {
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSubcontractorId) {
      toast({
        title: "Error",
        description: "Please select a subcontractor",
        variant: "destructive",
      });
      return;
    }

    if (selectedServices.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one service",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await assignSubcontractor({
        locationId,
        subcontractorId: selectedSubcontractorId as Id<"subcontractors">,
        services: selectedServices,
        status: status as "active" | "completed" | "scheduled",
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        notes: notes || undefined,
      });

      toast({
        title: "Success!",
        description: "Subcontractor assigned successfully",
      });

      // Call the callback to refresh data
      if (onSubcontractorAssigned) {
        onSubcontractorAssigned();
      }

      // Reset form
      setSelectedSubcontractorId("");
      setSelectedServices([]);
      setStatus("active");
      setStartDate("");
      setEndDate("");
      setNotes("");

      onClose();
    } catch (error) {
      console.error("Error assigning subcontractor:", error);
      toast({
        title: "Error",
        description: "Failed to assign subcontractor",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when sheet closes
  const handleClose = () => {
    setSelectedSubcontractorId("");
    setSelectedServices([]);
    setStatus("active");
    setStartDate("");
    setEndDate("");
    setNotes("");
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>Assign Subcontractor</SheetTitle>
          <SheetDescription>
            Assign a subcontractor to this location with specific services and details.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="subcontractor">
                Subcontractor <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedSubcontractorId} onValueChange={setSelectedSubcontractorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a subcontractor" />
                </SelectTrigger>
                <SelectContent>
                  {subcontractors.length === 0 ? (
                    <SelectItem value="no-subcontractors" disabled>
                      No active subcontractors found
                    </SelectItem>
                  ) : (
                    subcontractors.map((subcontractor: any) => (
                      <SelectItem key={subcontractor._id} value={subcontractor._id}>
                        {subcontractor.name} - {subcontractor.contactName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedSubcontractorId && (
              <div className="space-y-2">
                <Label>
                  Services <span className="text-red-500">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {availableServices.map((service: any) => (
                    <div key={service} className="flex items-center space-x-2">
                      <Checkbox
                        id={service}
                        checked={selectedServices.includes(service)}
                        onCheckedChange={() => handleServiceToggle(service)}
                      />
                      <Label htmlFor={service} className="text-sm">
                        {service}
                      </Label>
                    </div>
                  ))}
                </div>
                {selectedServices.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedServices.map((service) => (
                      <Badge key={service} variant="secondary">
                        {service}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes about this assignment..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Assigning..." : "Assign Subcontractor"}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
